// designTokens.ts
// This file contains the design tokens (colors, spacing, typography, etc.) for the admin-portal theme.

import type { ActivityGoalKind } from "../components/activityGoalKinds";
import type { DeviceStatus } from "../types/device";

export const color = {
  primary: "#415086",
  secondary: "#087BC4",
  secondary40: "#A8ADB8",
  typographyBase: "#363652", // Base color for typography throughout the app
  hover: "#313C6D",
  disabled: "rgba(65, 80, 134, 0.50)",
  background: "#F5F5F5",
  background2: "#F6F7F8",
  white: "#FFFFFF",
  gray: "#656565",
  black: "#000000",
  transparent: "transparent",

  // Secondary button colors
  secondaryButtonBackground: "#E0E6F2",
  secondaryButtonHover: "#D1DBF0",
  secondaryButtonDisabled: "#F0F0F0",
  secondaryButtonDisabledText: "#CCCCCC",

  // Datagrid colors
  datagridHeaderBackground: "rgba(54, 54, 82, 0.07)",
  datagridBorder: "#f0f0f0",

  // Panel border — visible enough to separate cards/rows from the page background.
  panelBorder: "#e6e8ee",

  // Selection state for clickable option rows.
  selectedOptionBackground: "#eef1fb",
  selectedOptionBorder: "#c2c8d2",

  // Status colors
  success: "#16A34A",
  error: "#DC2626",
  chipColor: "rgba(0, 0, 0, 0.08)",

  // Planned-activity state (recommendation rows marked as planned)
  plannedBorder: "#cfe9d8",
  plannedBackground: "#f4faf6",
  plannedText: "#1f7a44",
  plannedChipBackground: "#e4f4ea",
  plannedChipBorder: "#bfe6cd",

  // Progress bar track background
  progressTrack: "#e7eaf0",

  // Muted icon color (empty states)
  iconMuted: "#c4cad4",

  // Resource accents used by dashboard previews and summary cards.
  primaryTint: "#E9ECFA",
  primarySurface: "#F2F4FB",
  secondaryTint: "#EEF4FC",
  seniorAccent: "#6B46B5",
  seniorTint: "#EFE9FA",
  seniorSurface: "#F5F0FC",
};

// Per-category accent + tint colors for activity goals (progress bars and
// recommendation blocks). Keyed by ActivityGoalEnum value: `color` is the accent
// (bar / % / icon tint) taken from the existing category SVGs under /images so the
// progress accents match the icons already used in the Aktivitätenverlauf tab; `bg`
// is a soft tint of that color; `icon` is the SVG asset name.
export const activityCategoryStyle: Record<ActivityGoalKind, { color: string; bg: string; icon: string }> = {
  COGNITIVE: { color: "#43A047", bg: "#E9F6EC", icon: "cognitive" },
  PHYSICAL: { color: "#9D1C43", bg: "#F7E8ED", icon: "physical" },
  SOCIAL_AND_COMMUNICATIVE: { color: "#EF8614", bg: "#FDF0E1", icon: "social" },
  MENTAL_WELLBEING: { color: "#653DDE", bg: "#ECE7FB", icon: "wellbeing" },
};

// Status pill colors for an activity session: pending (not started), running
// (started, not ended) and done (ended). Taken from the procedure detail design.
export type ActivitySessionStatus = "pending" | "running" | "done";

export const sessionStatusStyle: Record<ActivitySessionStatus, { color: string; bg: string; border: string }> = {
  pending: { color: "#c06a10", bg: "#fdf1e3", border: "#f6dcbb" },
  running: { color: "#1c63b0", bg: "#e9f2fb", border: "#cfe2f6" },
  done: { color: "#1f7a44", bg: "#e4f4ea", border: "#bfe6cd" },
};

export const deviceStatusStyle: Record<DeviceStatus, { color: string; bg: string }> = {
  active: { color: color.plannedText, bg: color.plannedChipBackground },
  inactive: { color: color.gray, bg: color.background2 },
  "entered-in-error": { color: color.gray, bg: color.background2 },
  unknown: { color: color.gray, bg: color.background2 },
};

export const space = {
  xxs: 1,
  xs: 2,
  sm: 4,
  sm2: 6,
  md: 8,
  lg: 12,
  xl: 16,
  xxl: 24,
  xxl1: 32,
  xxl2: 36,
  xxxl: 56,
};

export const borderRadius = {
  none: 0,
  xxs: 1,
  xs: 2, // 2px
  sm: 4,
  sm2: 6,
  md: 8,
  panel: 18,
  lg: 48,
};

export const typography = {
  fontSize: {
    xxs: 12, // 12px (0.75rem) - for small text like subtitles
    caption: 13,
    xs: 14, // 14px (0.875rem) - for small text like subtitles
    sm: 15, // 15px when used with MUI (converts to rem)
    md: 16, // 16px when used with MUI (converts to rem)
    lg: 20,
    xl: 24, // 24px (1.5rem) - for large titles
    sectionHeading: 19,
    metric: 26,
    pageHeading: 30,
  },
  fontWeight: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
  fontFamily: "'Outfit', Arial, sans-serif",
  lineHeight: {
    xs: 1.2, // Tight - for headings
    sm: 1.4, // Normal - for body text
    md: 1.6, // Comfortable - for readable body text
    lg: 1.8, // Loose - for accessibility
    xl: 2.0, // Extra loose - for special cases
  },
  letterSpacing: {
    xs: "-0.5px", // Tight - for large headings
    sm: "0.15px", // Small - for body text
    md: "0.5px", // Medium - for buttons/labels
    lg: "1px", // Large - for caps/small text
    xl: "2px", // Extra large - for emphasis
  },
};

export const shadows = {
  secondary: "0 1px 5px 0 rgba(0, 0, 0, 0.12), 0 2px 2px 0 rgba(0, 0, 0, 0.14), 0 3px 1px -2px rgba(0, 0, 0, 0.20)",
  secondaryHover:
    "0 2px 8px 0 rgba(0, 0, 0, 0.16), 0 3px 4px 0 rgba(0, 0, 0, 0.18), 0 4px 2px -2px rgba(0, 0, 0, 0.24)",
  // Subtle panel elevation from the design — leans on the border, not the shadow.
  card: "0 1px 2px rgba(20, 30, 60, 0.06), 0 1px 3px rgba(20, 30, 60, 0.05)",
  dialog: "0 20px 45px rgba(0, 0, 0, 0.22)",
  none: "none",
};

export const maxWidth = {
  xs: 170,
  md: 600,
};
export const avatarSize = {
  xs: 32,
  sm: 70,
  md: 96,
  lg: 100,
};

export const avatarBadgeSize = {
  sm: 24,
  md: 35,
};

export const iconSize = {
  small: 16,
  medium: 20,
  large: 24,
};

export const ContainerMaxWidth = {
  sm: 500,
};

export const buttonMinWidth = {
  sm: 120,
};

export const theme = {
  color,
  space,
  borderRadius,
  typography,
  shadows,
  maxWidth,
  iconSize,
} as const;

export const lineHeight = {
  xxs: 1.3,
};

export type ThemeColor = keyof typeof color;
export type ThemeSpacing = keyof typeof space;
export type ThemeBorderRadius = keyof typeof borderRadius;
export type IconSize = keyof typeof iconSize;
